import {Button, Col, Container, Modal, Row} from "react-bootstrap";
import React, {Fragment, useContext, useEffect, useRef, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import {UsacmContext} from "../App";
import {addCourseInstructor, getCourseInstructorList, removeCourseInstructor} from "../api/CourseApi";
import {UserSelect} from "../shared/UserSelect";
import {toolTipRenderer} from "../utils/gridUtils";
import {getConfLabel, showSuccessToast, titleCase} from "../utils/usacmUtils";
import {getErrorMessageForField} from "../utils/formUtils";
import {CONF_LABEL_KEY_COURSE} from "../constants";

/**
 * The instructors on one course.  An instructor can edit the content of the course they are assigned to (details,
 * pages, docs, videos - not prices) and gets the course settings menu option - see courses_utils.can_edit_course().
 * NOTE: only a course admin (or conf admin / staff) can say who the instructors are, everyone else who can
 * see this course gets a read-only list.
 */
export function CourseInstructors({course, canAssign, onClose}) {
  const context = useContext(UsacmContext);
  const [conf,] = context.conference;
  const [instructorList, setInstructorList] = useState([]);
  const [newUserId, setNewUserId] = useState(null);
  const [removingInstructor, setRemovingInstructor] = useState(null);
  const [errors, setErrors] = useState([]);
  const gridRef = useRef();
  const hasFewRows = (instructorList || []).length < 12;
  const courseLabel = titleCase(getConfLabel(conf, CONF_LABEL_KEY_COURSE));
  const actionColWidth = canAssign ? 110 : 0;

  function loadInstructors() {
    if (!course?.id) {
      return;
    }
    getCourseInstructorList(course.id, (code, data, newErrors) => {
      if (code === 200) {
        setInstructorList(data);
        setErrors([]);
      } else {
        setErrors(newErrors);
      }
    });
  }

  useEffect(() => {
    loadInstructors();
  }, [course?.id]);

  function callAddInstructor() {
    addCourseInstructor(course?.id, newUserId, (code, data, newErrors) => {
      if (code === 200) {
        showSuccessToast('Added instructor.');
        setInstructorList(data);
        setNewUserId(null);
        setErrors([]);
      } else {
        setErrors(newErrors);
      }
    });
  }

  function callRemoveInstructor() {
    removeCourseInstructor(course?.id, removingInstructor?.user_id, (code, data, newErrors) => {
      setRemovingInstructor(null);
      if (code === 200) {
        showSuccessToast('Removed instructor.');
        setInstructorList(data);
        setErrors([]);
      } else {
        setErrors(newErrors);
      }
    });
  }

  // Column setup
  const colDefs = [
    {
      headerName: 'Name', flex: 3, minWidth: 150, cellRenderer: toolTipRenderer,
      valueGetter: params => `${params.data?.first_name || ''} ${params.data?.last_name || ''}`.trim(),
    },
    {field: 'email', cellRenderer: toolTipRenderer, headerName: 'Email', flex: 3, minWidth: 150},
    {field: 'affiliation', cellRenderer: toolTipRenderer, headerName: 'Affiliation', flex: 3, minWidth: 120},
  ];
  if (canAssign) {
    colDefs.push({
      headerName: 'Action',
      sortable: false, filter: false,
      maxWidth: actionColWidth, minWidth: actionColWidth,
      cellRenderer: params => {
        return <Fragment>
          <Button type="button" variant="danger" onClick={() => setRemovingInstructor(params.data)} size="sm">
            Remove
          </Button>
        </Fragment>;
      }
    });
  }

  const defaultColDefs = {
    sortable: true,
    filter: false,
    resizable: true,
  }

  // A course which has not been saved yet has no id, so there is nowhere to hang an instructor
  if (!course?.id) {
    return (
      <Container fluid className="usacm-container-wide">
        <Row>
          <Col className="text-center mt-3">
            Save this {courseLabel.toLowerCase()} before adding instructors.
          </Col>
        </Row>
        <Row>
          <Col className="usacm-button-row mt-3">
            <Button variant="secondary" onClick={() => onClose()}>Course List</Button>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className="usacm-container-wide">
      <Row>
        <Col className="mb-3">
          Instructors can edit this {courseLabel.toLowerCase()}, but cannot create or
          delete {courseLabel.toLowerCase()}s or change who the instructors are.
        </Col>
      </Row>

      <Row>
        <Col>
          <div className="ag-theme-alpine" style={{width: '100%', ...(hasFewRows ? {} : {height: '700px'})}}>
            <AgGridReact
              ref={gridRef}
              rowData={instructorList}
              columnDefs={colDefs}
              defaultColDef={defaultColDefs}
              gridOptions={{enableCellTextSelection: true, ensureDomOrder: true}} // enable cell selection
              domLayout={hasFewRows ? "autoHeight" : "normal"}
              getRowId={(params) => params.data.id} // prevent re-rendering the whole table when data changes
            />
          </div>
        </Col>
      </Row>

      {getErrorMessageForField(errors, '') &&
        <Row>
          <Col className="text-center mt-3 usacm-error-message">
            {getErrorMessageForField(errors, '')}
          </Col>
        </Row>
      }

      {canAssign &&
        <Row className="mt-3">
          <Col>
            <UserSelect userId={newUserId}
                        onChange={user => setNewUserId(user?.id)}
                        isInvalid={!!getErrorMessageForField(errors, 'userid')}
                        noneSelectedText="Choose an instructor..."/>
            <div className="usacm-invalid-feedback">
              {getErrorMessageForField(errors, 'userid')}
            </div>
          </Col>
          <Col xs="auto">
            <Button type="button" disabled={!newUserId} onClick={() => callAddInstructor()}>Add Instructor</Button>
          </Col>
        </Row>
      }

      <Row>
        <Col className="usacm-button-row mt-3">
          <Button variant="secondary" onClick={() => onClose()}>Course List</Button>
        </Col>
      </Row>

      <Modal show={!!removingInstructor}
             onHide={() => setRemovingInstructor(null)}
             size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Confirm Remove</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to remove {removingInstructor?.first_name} {removingInstructor?.last_name} as
          an instructor? They will no longer be able to edit this {courseLabel.toLowerCase()}.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setRemovingInstructor(null)}>Cancel</Button>
          <Button variant="danger" onClick={() => callRemoveInstructor()}>Remove Instructor</Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
}
